import { reduce } from 'lodash';
import { AccountType } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { isEmptyVal, safeJSONParse } from '@/utils';
import { couponSourceTypeMap } from '@/views/config/coupon/helper/constant';
import { BillingProductEntryUtils } from '@zoom/fe-billing-components';
import { filterNotSupportPlanCodesForThresholdCoupon } from '@/views/config/coupon/helper/utils';

const getCurrentFlowType = (config = {}) => {
  const accountType = config?.accountType;
  const isFreeAccount = [AccountType.ACCOUNT_FREE_WITH_CC, AccountType.ACCOUNT_FREE_WITHOUT_CC].includes(accountType);
  // todo with paid
  return config?.pageConfig?.type || (isFreeAccount ? flowTypes.basic2pro : flowTypes.buyNow);
};

/**
 *
 * @param planCodeList
 * @param defaultValue
 * @return {PreloadDiscountMapBean}
 */
export const transferPlanCodeListToDiscountInfo = (planCodeList = [], defaultValue = {}) => {
  return reduce(planCodeList, (res, planCode) => {
    if (planCode) res[planCode] = { ...defaultValue, planCode };
    return res;
  }, {});
};

/**
 * Process marketing coupon data and transform plan code lists to discount info
 * @param {Object} couponData - Parsed coupon data
 * @returns {Object} Processed coupon data with discountInfo
 */
const processMarketingCoupon = (couponData) => {
  if (!couponData) return null;

  const { couponSourceType, supportPlanCodeList = [], defaultPlanCodeList = [] } = couponData;

  // Process threshold coupon: filter and transform support plan codes
  if (couponSourceType === couponSourceTypeMap.threshold) {
    const filteredSupportPlanCodeList = filterNotSupportPlanCodesForThresholdCoupon(supportPlanCodeList);
    couponData.discountInfo = transferPlanCodeListToDiscountInfo(filteredSupportPlanCodeList);
  }

  // Always set default discount info
  couponData.defaultDiscountInfo = transferPlanCodeListToDiscountInfo(defaultPlanCodeList);

  return couponData;
};

/**
 * Extract campaign entry data from trialCampaignInfo
 * Supports both campaignList array and flat structure for backward compatibility
 * @param {Object} trialCampaignInfo - Trial campaign info object
 * @returns {Object|null} Campaign entry data or null
 */
const getCampaignEntryData = (trialCampaignInfo) => {
  if (!trialCampaignInfo) return null;

  // Priority 1: Use campaignList array if exists
  if (!isEmptyVal(trialCampaignInfo.campaignList)) {
    return trialCampaignInfo.campaignList[0];
  }

  // Priority 2: Use flat structure if campaignCode exists
  if (trialCampaignInfo.campaignCode) {
    return trialCampaignInfo;
  }

  return null;
};

/**
 * Merge pageConfig with new config data
 * @param {Object} config - Current config
 * @param {Object} newPageConfig - New page config to merge
 * @param {boolean} setFlowType - Whether to set flow type if not exists
 * @returns {Object} Updated config
 */
const mergePageConfig = (config, newPageConfig = {}, setFlowType = false) => {
  const pageConfig = {
    ...config.pageConfig,
    ...newPageConfig,
    // Only set flow type if not already set and setFlowType is true
    ...(setFlowType && !config.pageConfig?.type ? { type: getCurrentFlowType(config) } : {}),
  };

  return {
    ...config,
    pageConfig,
  };
};

/**
 * Process trial campaign info and update page config
 * @param {Object} config - Configuration object
 * @returns {Object} Updated configuration with pageConfig
 */
const processTrialCampaignInfo = (config) => {
  const campaignEntryData = getCampaignEntryData(config.trialCampaignInfo);

  if (!campaignEntryData) {
    return config;
  }

  try {
    const result = BillingProductEntryUtils.syncPreCheckForProductEntry({
      entryOptions: campaignEntryData
    });

    // Merge pageQueryConfig if exists
    if (result?.pageQueryConfig) {
      return mergePageConfig(config, result.pageQueryConfig);
    }
  } catch (error) {
    // Log error but don't break the flow
    console.error('Failed to process trialCampaignInfo:', error);
  }

  return config;
};

/**
 * Pre config preload coupon data with newBillingConfig
 * Handles both marketingCoupon and trialCampaignInfo scenarios
 * @param {Object} config - Configuration object
 * @returns {Object} Processed configuration
 */
export const preConfigWithCampaign = (config = {}) => {
  // Early return: If marketingCoupon was already processed by preConfigWithPartnerRefer,
  // skip marketingCoupon processing and go directly to trialCampaignInfo
  // This optimization avoids duplicate processing
  if (config.marketingCoupon === null && config.partnerReferral?.coupon) {
    return processTrialCampaignInfo(config);
  }

  // Handle marketing coupon scenario (only if not processed by preConfigWithPartnerRefer)
  if (!isEmptyVal(config.marketingCoupon)) {
    try {
      const couponData = safeJSONParse(config.marketingCoupon);
      const processedCouponData = processMarketingCoupon(couponData);

      if (processedCouponData) {
        return mergePageConfig(
          { ...config, marketingCoupon: processedCouponData },
          {},
          true
        );
      }
    } catch (error) {
      // Log error but continue to process trialCampaignInfo
      console.error('Failed to process marketingCoupon:', error);
    }
  }

  // Handle trial campaign info scenario
  return processTrialCampaignInfo(config);
};

/**
 * Pre config with cart ID for preloaded shopping cart
 * @param {Object} config - Configuration object
 * @returns {Object} Processed configuration
 */
export const preConfigWithCartId = (config = {}) => {
  /** @type {PreloadWithCartIdResultDto} */
  const preloadCartInfo = config?.preloadCartInfo;

  if (!isEmptyVal(preloadCartInfo)) {
    return mergePageConfig(
      config,
      preloadCartInfo.configData || {},
      true
    );
  }

  return config;
};
